import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { getFilteredList, getValueFromResource, sortData } from "../../common/timeLiveFunction";
import Loader from "../../common/Loader";
import parse from 'html-react-parser';
import { ShowToast } from "../../common/Toastr";
import { getAccountEmployeeDashboardsAsync, deleteAccountEmployeeDashboardAsync, updateIsLoading, addOrUpdateDashboardAsync, getPagePermissions } from "./dashboardSlice";
import { toAbsoluteUrl } from "../../_metronic/helpers";
import { t } from "i18next";
import { ShowSwalWithConfirmation } from "../../common/SweetAlert";
import { useNavigate } from "react-router-dom";
import AddDashboardModal from "./AddDashboardModal";
import fetchTimeliveAPI from "../../common/fetchTimeliveAPI";
import { Tooltip as ReactTooltip } from "react-tooltip";

const DashboardSummary = () => {
  const dispatch = useAppDispatch();
  const { dashboardsList, isLoading, isReload } = useAppSelector((state) => state.dashboard);
  const [sortConfig, setSortConfig] = useState({ column: "DashboardName", order: "asc" });
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [accountDashboardData, setAccountDashboardData] = useState({});
  const navigate = useNavigate();
  const [template, setTemplate] = useState<any[]>([]);
  const [permissions, setPermissions] = useState({
    IsList: false,
    IsAdd: false,
    IsEdit: false,
    IsDelete: false,
  });

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const res = await dispatch(getPagePermissions(25));
        if (res.meta?.requestStatus === 'fulfilled') {
          setPermissions({
            IsList: res?.payload?.IsList ?? false,
            IsAdd: res?.payload?.IsAdd ?? false,
            IsEdit: res?.payload?.IsEdit ?? false,
            IsDelete: res?.payload?.IsDelete ?? false,
          });
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchPermissions();
    getDashboardTemplates();
  }, []);

  const getDashboardTemplates = async () => {
    try {
      const response = await fetchTimeliveAPI("api/AccountDefaultDashboard/GetAccountDashboardTemaplets", "GET", "", true);
      const json = await response.json();
      const filteredData = json?.Data?.filter((item: any) => [2, 3, 4].includes(item.SystemDashboardTemplateId));
      setTemplate(filteredData);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    getDashboardTemplates();
    dispatch(getPagePermissions(25));
  }, [])

  useEffect(() => {
    dispatch(getAccountEmployeeDashboardsAsync())
      .unwrap()
      .catch(() => ShowToast("Error", "Failed to fetch dashboards", "error", "toast-top-right"));
    dispatch(updateIsLoading(false));

  }, [dispatch, isReload]);

  const openModal = (template: any) => {
    setSelectedTemplate(template);
    setAccountDashboardData({});
    setShowModal(true);
  };

  const handleSorting = (column: string) => {
    setSortConfig((prev) => ({
      column,
      order: prev.column === column && prev.order === "asc" ? "desc" : "asc",
    }));
  };

  const onDeleteClick = (id: any) => {
    ShowSwalWithConfirmation('Are you sure you want to delete this record?', "")
      .then((result) => {
        dispatch(updateIsLoading(true));
        dispatch(deleteAccountEmployeeDashboardAsync(id))
          .unwrap()
          .then((res) => {
            if (res?.StatusCode === '200') {
              ShowToast("Success", res?.ResponseMessage, "success", "toast-top-right")
            } else {
              ShowToast("Error", res?.ResponseMessage, "error", "toast-top-right")
            }
          })
          .catch(() => ShowToast("Error", "Failed to delete item", "error", "toast-top-right"));

      })
      .catch((error) => {
      });
  };

  const dashboardsArray = Array.isArray(dashboardsList) ? dashboardsList : [];
  const filteredItems = getFilteredList(dashboardsArray, searchTerm);
  const sortedItems = sortData(filteredItems, sortConfig.order, sortConfig.column);

  const indexOfLastItem = currentPage * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;
  const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);

  const navigateToDashboardView = (obj: any) => {
    if (obj.DashboardName === "Default Dashboard") {
      navigate("/dashboard");
    } else {
      navigate(`/dashboard/${obj.AccountEmployeeDashboardsId}`, { state: { obj } });
    }
  };

  const handleEdit = (item: any) => {
    setAccountDashboardData(item);
    setSelectedTemplate({
      SystemDashboardTemplateId: item.SystemDashboardTemplateId,
      title: item.SystemDashboardTemplateName
    });
    setShowModal(true);
  }

  const handlePinDashboard = async (item: any) => {
    if (!permissions?.IsEdit) return;
    try {
      const payload = {
        AccountEmployeeDashboardsId: item?.AccountEmployeeDashboardsId,
        DashboardName: item?.DashboardName,
        IsDefault: item?.IsDefault,
        IsFavourite: !item?.IsFavourite,
        SystemDashboardTemplateId: item?.SystemDashboardTemplateId
      }
      const response = await dispatch(addOrUpdateDashboardAsync(payload));
      if (response?.payload?.StatusCode === '200') {
        ShowToast(response.payload?.ResponseMessage, "Success", "success", "toast-top-right");
      } else {
        ShowToast(response.payload?.ResponseMessage, "Error", "error", "toast-top-right");
      }
    } catch (error) {
      console.log(error);
    }
  }

  const handleSetDefaultDashboard = async (item: any) => {
    if (!permissions?.IsEdit) return;
    if (item?.IsDefault) {
      ShowToast("Warning", "Already set as default dashboard", "warning", "toast-top-right");
      return;
    }
    try {
      const payload = {
        AccountEmployeeDashboardsId: item?.AccountEmployeeDashboardsId,
        DashboardName: item?.DashboardName,
        IsDefault: true,
        IsFavourite: item?.IsFavourite,
        SystemDashboardTemplateId: item?.SystemDashboardTemplateId
      }
      const response = await dispatch(addOrUpdateDashboardAsync(payload));
      if (response?.payload?.StatusCode === '200') {
        ShowToast(response.payload?.ResponseMessage, "Success", "success", "toast-top-right");
      } else {
        ShowToast(response.payload?.ResponseMessage, "Error", "error", "toast-top-right");
      }
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <>  {isLoading && <Loader />}
      <div className="card card-custom admin-card">
        <div className="card-header card-drop sticky-top bg-white fixed-top">
          <h3 className="card-title fw-bold">{getValueFromResource('Dashboard Summary', dispatch, t)}</h3>
          {permissions?.IsAdd && <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/dashboardTemplate")}
          >
            {getValueFromResource('Add Dashboard', dispatch, t)}
          </button>}
        </div>
        <div className="row col-md-12 col-sm-12 col-xs-12 navBarParentDiv mt-10 mb-10">
          {template.map((template, index) => (
            <div className="col-md-4 col-sm-4 col-xs-12" key={index}>
              <div
                className="cardInfoShadowDashboard"
                // style={{ cursor: "pointer" }}
                style={{
                  cursor: template.IsFeatureEnabled ? "pointer" : "not-allowed",
                  opacity: template.IsFeatureEnabled ? 1 : 0.6,
                  filter: template.IsFeatureEnabled ? "none" : "grayscale(1)",
                  pointerEvents: template.IsFeatureEnabled ? "auto" : "all",
                  overflow: 'hidden'
                }}
                onClick={() => {
                   if (!permissions?.IsAdd || !template.IsFeatureEnabled) return;
                  openModal(template)
                }}
              >
                <div className="esNavBarInnerBoxParent">
                  <div className="tsNavBarInnerBoxFirstSection">
                    <div className="tsNavBarInnerBoxFirstInner" />
                    <div className="symbol symbol-48px me-3">
                      {template.SystemDashboardTemplateIconPath ? (
                        <img src={toAbsoluteUrl(template.SystemDashboardTemplateIconPath)} alt={template.SystemDashboardTemplateName} />
                      ) : (
                        <span className="fw-bold fs-4 text-muted">+</span>
                      )}
                    </div>
                  </div>
                  <div className="esNavBarInnerBoxSecondSection">
                    <a className="fw-bold d-block fs-5 dbNavBarInfoText">{getValueFromResource(`${template.SystemDashboardTemplateName}`, dispatch, t)}</a>
                    {<span className="fs-7 dbNavBarInfoLabel"style={{lineHeight: 1, marginTop: '5px',fontSize: "12px",display: "-webkit-box",WebkitLineClamp: 2,WebkitBoxOrient: "vertical",overflow: "hidden",textOverflow: "ellipsis",wordBreak: "break-word",maxWidth: "100%"}}>{getValueFromResource(`${template.SystemDashboardTemplateDescription}`, dispatch, t)}</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card-body table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
          <table className="table table-row-bordered  align-middle gs-1 gy-1 gx-1" >
            <thead>
              <tr className="thHeading align-middle cursor-pointer " >
                <th className='w-25px'
                  style={{ width: "25%" }}
                  onClick={() => handleSorting("DashboardName")}
                >
                  {getValueFromResource("Dashboard Name", dispatch, t)} {sortConfig.column === "DashboardName" ? (sortConfig.order === "asc" ? "↑" : "↓") : ""}
                </th>
                <th style={{ width: "20%" }}>{getValueFromResource("Template", dispatch, t)}</th>
                <th style={{ width: "20%" }}>{getValueFromResource("Modified On", dispatch, t)}</th>
                <th style={{ width: "15%" }}>{getValueFromResource("Owner", dispatch, t)}</th>
                <th style={{ width: "15%" }}>{getValueFromResource("Created Time Ago", dispatch, t)}</th>
                <th style={{ width: "5%" }}>{getValueFromResource("Actions", dispatch, t)}</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center">
                    No Record Found
                  </td>
                </tr>
              )}
              {currentItems.map((item: any, index: number) => (
                <tr key={index}>
                  <td style={{ cursor: "pointer", color: "inherit", textDecoration: "none" }}>
                    <div className="d-flex align-items-center">
                      <span
                        onClick={() =>
                          navigateToDashboardView({
                            AccountEmployeeDashboardsId: item.AccountEmployeeDashboardsId,
                            DashboardName: item.DashboardName
                          })
                        }
                      >
                        {item.DashboardName}
                      </span>
                      <div className="d-flex align-items-center gap-2 ms-3">
                        <i
                          className={`bi ${item.IsFavourite ? "bi-pin-fill text-primary" : "bi-pin-fill"} fs-6`}
                          style={{ marginTop: '3px' }}
                          data-tooltip-id="dashboard-tooltip"
                          data-tooltip-content={getValueFromResource("Favourite", dispatch, t)}
                          data-tooltip-place="top"
                          onClick={() => handlePinDashboard(item)}></i>
                        <i
                          className={`bi ${item.IsDefault ? "bi-check-circle-fill text-success" : "bi-check-circle"} fs-6`}
                          style={{ marginTop: '1px' }}
                          data-tooltip-id="dashboard-tooltip"
                          data-tooltip-content={getValueFromResource("Default", dispatch, t)}
                          data-tooltip-place="top"
                          onClick={() => handleSetDefaultDashboard(item)}></i>
                      </div>

                    </div>
                  </td>

                  <td>{item.SystemDashboardTemplateName}</td>
                  <td>{item.ModifiedTimeAgo}</td>
                  <td><span style={{ color: '#7b8ba6', fontSize: 11 }}>
                    <div className='d-flex align-items-center flex-shrink-0 me-5'>
                      <div className='symbol-group symbol-hover flex-shrink-0 me-2'>
                        {item.CreatedByName != null && parse(`${item.CreatedByName}`)}
                      </div>
                    </div>
                  </span></td>
                  <td>{item.CreatedTimeAgo}</td>

                  <td className="text-center">
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="transparent"
                        className="btn btn-icon btn-active-light-dark btn-sm"
                      >
                        <i className="bi bi-three-dots fs-7"></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 px-1 py-1 fs-2 w-150px">
                        {permissions?.IsEdit && <div className='menu-hover-bg-sec'>
                          <Dropdown.Item
                            id="kt_OffCanvas"
                            className="menu-item me-lg-0"
                            onClick={() => handleEdit(item)}
                          >
                            <span className="menu-icon custom-menu mb-2">
                              <i className="bi bi-pencil-square fs-6 iconColor allign">
                                <span className="path1"></span>
                                <span className="path2"></span>
                              </i>
                            </span>
                            <span className="menu-title custom-menu-fontsize">
                              {getValueFromResource('Edit', dispatch, t)}
                            </span>
                          </Dropdown.Item>
                        </div>}
                        {(!item?.IsDefault && permissions?.IsDelete) && <>
                          <div className='separator mb-1 mt-1 opacity-75'></div>
                          <Dropdown.Item
                            className="menu-item me-lg-0"
                            onClick={() => onDeleteClick(item.AccountEmployeeDashboardsId)}
                          >
                            <span className="menu-icon custom-menu">
                              <i className="bi bi-trash fs-6 delete-menu-icon-color align">
                                <span className="path1"></span>
                                <span className="path2"></span>
                              </i>
                            </span>
                            <span className="menu-title delete-menu custom-menu-fontsize">
                              {getValueFromResource('Delete', dispatch, t)}
                            </span>
                          </Dropdown.Item>
                        </>}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {showModal && <AddDashboardModal
          template={selectedTemplate}
          onClose={() => setShowModal(false)}
          accountDashboardData={accountDashboardData}
        />}
      </div>
      <ReactTooltip
        id="dashboard-tooltip"
        place="top"
        style={{ fontSize: "12px", fontWeight: "normal", padding: "8px 12px", borderRadius: "6px", backgroundColor: "#1F2937", color: "white", zIndex: 999999, maxWidth: "250px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
      />
    </>
  );
};

export default DashboardSummary;